<template>
  <div class="commodity-page">
    <!-- Header with title and action buttons -->
    <div class="flex align-items-center justify-content-between mb-4">
      <h2 class="text-2xl font-semibold m-0">Commodity Types</h2>
      <div class="flex gap-2">
        <Button
          v-if="favoriteCommodities.length > 1"
          label="Manage Favorites Order"
          icon="pi pi-sort"
          class="p-button-outlined"
          @click="openFavoritesOrderDialog"
        />
        <Button label="New Commodity" icon="pi pi-plus" @click="openCreateDialog" />
      </div>
    </div>

    <!-- Commodities Table -->
    <Card>
      <template #content>
        <CommodityList
          :commodities="commodities"
          :loading="loading"
          @edit-commodity="openEditDialog"
          @delete-commodity="confirmDelete"
          @toggle-favorite="toggleFavorite"
        />
      </template>
    </Card>

    <!-- Create Commodity Dialog -->
    <Dialog
      v-model:visible="showCreateDialog"
      :style="{ width: '450px' }"
      header="Create New Commodity"
      :modal="true"
      :closable="false"
    >
      <CommodityCreateForm
        :submitted="submitted"
        :initial-commodity="newCommodity"
        @update:commodity="newCommodity = $event"
      />
      <template #footer>
        <Button
          label="Cancel"
          icon="pi pi-times"
          class="p-button-text"
          @click="closeCreateDialog"
        />
        <Button label="Save" icon="pi pi-check" @click="saveCommodity" />
      </template>
    </Dialog>

    <!-- Edit Commodity Dialog -->
    <Dialog
      v-model:visible="showEditDialog"
      :style="{ width: '450px' }"
      header="Edit Commodity"
      :modal="true"
      :closable="false"
    >
      <CommodityEditForm
        :submitted="submitted"
        :initial-commodity="selectedCommodity"
        @update:commodity="selectedCommodity = $event"
      />
      <template #footer>
        <Button label="Cancel" icon="pi pi-times" class="p-button-text" @click="closeEditDialog" />
        <Button label="Update" icon="pi pi-check" @click="updateCommodity" />
      </template>
    </Dialog>

    <!-- Manage Favorites Order Dialog -->
    <Dialog
      v-model:visible="showFavoritesOrderDialog"
      :style="{ width: '600px' }"
      header="Manage Favorites Order"
      :modal="true"
      :closable="false"
    >
      <div class="mb-3">
        <p class="text-500 mb-0">
          Use the arrows to reorder your favorite commodities. The order will be reflected in
          transaction forms.
        </p>
      </div>

      <OrderList
        v-model="favoriteCommoditiesOrdered"
        dataKey="id"
        class="favorites-orderlist"
        style="width: 100%"
        width="100%"
      >
        <template #option="{ option }">
          <div class="flex align-items-center justify-content-between w-full">
            <div>
              <div class="font-semibold">{{ option.name }}</div>
              <div class="text-sm text-500">{{ option.category }}</div>
            </div>
          </div>
        </template>
      </OrderList>

      <template #footer>
        <Button
          label="Cancel"
          icon="pi pi-times"
          class="p-button-text"
          @click="closeFavoritesOrderDialog"
        />
        <Button
          label="Save Order"
          icon="pi pi-check"
          @click="saveFavoritesOrder"
          :loading="savingOrder"
        />
      </template>
    </Dialog>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted } from 'vue'
import { Timestamp } from 'firebase/firestore'
import type { CommodityType } from '@/types/CommodityType'
import { useCommodityTypeService } from '@/services/commodityTypeService'
import { useToast } from 'primevue/usetoast'
import { useConfirm } from 'primevue/useconfirm'

// Components
import CommodityList from '@/components/commodity/CommodityList.vue'
import CommodityCreateForm from '@/components/commodity/CommodityCreateForm.vue'
import CommodityEditForm from '@/components/commodity/CommodityEditForm.vue'
import Card from 'primevue/card'
import Button from 'primevue/button'
import Dialog from 'primevue/dialog'
import OrderList from 'primevue/orderlist'

const toast = useToast()
const confirm = useConfirm()

// State
const commodities = ref<CommodityType[]>([])
const loading = ref(true)
const showCreateDialog = ref(false)
const showEditDialog = ref(false)
const showFavoritesOrderDialog = ref(false)
const submitted = ref(false)
const savingOrder = ref(false)

// Form state
const newCommodity = ref<Partial<CommodityType>>({
  name: '',
  description: '',
  category: '',
  current_price_per_lb: 0,
  is_active: true,
  is_favorite: false,
})

const selectedCommodity = ref<CommodityType>({
  id: '',
  name: '',
  description: '',
  category: '',
  current_price_per_lb: 0,
  is_active: true,
  is_favorite: false,
  created_at: new Date(),
  updated_at: new Date(),
  is_deleted: false,
})

// Computed properties
const favoriteCommodities = computed(() => {
  return commodities.value.filter((commodity) => commodity.is_favorite)
})

const favoriteCommoditiesOrdered = ref<any[]>([])

// Services
const commodityTypeService = useCommodityTypeService()

// Watch for changes in favoriteCommodities to update the ordered list
watch(
  favoriteCommodities,
  (newFavorites: any[]) => {
    favoriteCommoditiesOrdered.value = [...newFavorites].sort((a: any, b: any) => {
      const orderA = a.favorite_order || 0
      const orderB = b.favorite_order || 0
      return orderA - orderB
    })
  },
  { immediate: true },
)

// Fetch commodities
async function fetchCommodities() {
  loading.value = true
  try {
    commodities.value = await commodityTypeService.getActiveCommodityTypes()

    // Debug: Log favorites with their order
    const favorites = commodities.value.filter((c: any) => c.is_favorite)
    console.log(
      'Favorites loaded:',
      favorites.map((f: any) => ({
        name: f.name,
        favorite_order: f.favorite_order,
      })),
    )
  } catch (error) {
    console.error('Error fetching commodities:', error)
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'Failed to load commodities',
      life: 3000,
    })
  } finally {
    loading.value = false
  }
}

// Dialog handlers
function openCreateDialog() {
  newCommodity.value = {
    name: '',
    description: '',
    category: '',
    current_price_per_lb: 0,
    is_active: true,
    is_favorite: false,
  }
  submitted.value = false
  showCreateDialog.value = true
}

function closeCreateDialog() {
  showCreateDialog.value = false
}

function openEditDialog(commodity: CommodityType) {
  selectedCommodity.value = { ...commodity }
  submitted.value = false
  showEditDialog.value = true
}

function closeEditDialog() {
  showEditDialog.value = false
}

// CRUD operations
async function saveCommodity() {
  submitted.value = true

  if (
    !newCommodity.value.name ||
    !newCommodity.value.category ||
    !newCommodity.value.current_price_per_lb
  ) {
    toast.add({
      severity: 'error',
      summary: 'Validation Error',
      detail: 'Please fill in all required fields',
      life: 3000,
    })
    return
  }

  try {
    const commodityData = {
      ...newCommodity.value,
      is_favorite: false,
      is_deleted: false,
    }

    await commodityTypeService.createCommodityType(commodityData)

    toast.add({
      severity: 'success',
      summary: 'Success',
      detail: 'Commodity created successfully',
      life: 3000,
    })

    closeCreateDialog()
    fetchCommodities()
  } catch (error) {
    console.error('Error creating commodity:', error)
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'Failed to create commodity',
      life: 3000,
    })
  }
}

async function updateCommodity() {
  submitted.value = true

  if (
    !selectedCommodity.value.name ||
    !selectedCommodity.value.category ||
    !selectedCommodity.value.current_price_per_lb
  ) {
    toast.add({
      severity: 'error',
      summary: 'Validation Error',
      detail: 'Please fill in all required fields',
      life: 3000,
    })
    return
  }

  if (!selectedCommodity.value.id) {
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'Commodity ID is missing',
      life: 3000,
    })
    return
  }

  try {
    await commodityTypeService.updateCommodityType(selectedCommodity.value.id, {
      name: selectedCommodity.value.name,
      description: selectedCommodity.value.description,
      category: selectedCommodity.value.category,
      current_price_per_lb: selectedCommodity.value.current_price_per_lb,
      is_active: selectedCommodity.value.is_active,
    })

    toast.add({
      severity: 'success',
      summary: 'Success',
      detail: 'Commodity updated successfully',
      life: 3000,
    })

    closeEditDialog()
    fetchCommodities()
  } catch (error) {
    console.error('Error updating commodity:', error)
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'Failed to update commodity',
      life: 3000,
    })
  }
}

function confirmDelete(commodity: CommodityType) {
  confirm.require({
    message: `Are you sure you want to delete "${commodity.name}"?`,
    header: 'Delete Confirmation',
    icon: 'pi pi-exclamation-triangle',
    acceptClass: 'p-button-danger',
    accept: () => deleteCommodity(commodity),
    reject: () => {},
  })
}

async function deleteCommodity(commodity: CommodityType) {
  if (!commodity.id) {
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'Commodity ID is missing',
      life: 3000,
    })
    return
  }

  try {
    await commodityTypeService.deleteCommodityType(commodity.id)

    toast.add({
      severity: 'success',
      summary: 'Success',
      detail: 'Commodity deleted successfully',
      life: 3000,
    })

    fetchCommodities()
  } catch (error) {
    console.error('Error deleting commodity:', error)
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'Failed to delete commodity',
      life: 3000,
    })
  }
}

async function toggleFavorite(commodity: CommodityType) {
  if (!commodity.id) {
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'Commodity ID is missing',
      life: 3000,
    })
    return
  }

  try {
    await commodityTypeService.updateCommodityType(commodity.id, {
      is_favorite: !commodity.is_favorite,
    })

    // Update the local state
    const index = commodities.value.findIndex((c) => c.id === commodity.id)
    if (index !== -1) {
      ;(commodities.value[index] as any) = {
        ...commodities.value[index],
        is_favorite: !commodity.is_favorite,
      }
    }

    toast.add({
      severity: 'success',
      summary: 'Success',
      detail: `Commodity ${!commodity.is_favorite ? 'added to' : 'removed from'} favorites`,
      life: 3000,
    })
  } catch (error) {
    console.error('Error toggling favorite:', error)
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'Failed to update favorite status',
      life: 3000,
    })
  }
}

// Utility functions
function formatCurrency(value: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
  }).format(value)
}

// Favorites order management
function openFavoritesOrderDialog() {
  showFavoritesOrderDialog.value = true
}

function closeFavoritesOrderDialog() {
  showFavoritesOrderDialog.value = false
}

async function saveFavoritesOrder() {
  savingOrder.value = true

  try {
    // Debug: Log what we're about to save
    console.log(
      'Saving favorites order:',
      favoriteCommoditiesOrdered.value.map((c: any, i: number) => ({
        name: c.name,
        newOrder: i,
        currentOrder: c.favorite_order,
      })),
    )

    // Save the new order to the database
    for (let index = 0; index < favoriteCommoditiesOrdered.value.length; index++) {
      const commodity = favoriteCommoditiesOrdered.value[index]
      if (commodity.id) {
        await commodityTypeService.updateCommodityType(commodity.id, {
          // Note: favorite_order is not stored in the database
        })
      }
    }

    // Update local state
    await fetchCommodities()

    toast.add({
      severity: 'success',
      summary: 'Success',
      detail: 'Favorites order saved successfully',
      life: 3000,
    })

    closeFavoritesOrderDialog()
  } catch (error) {
    console.error('Error saving favorites order:', error)
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'Failed to save favorites order',
      life: 3000,
    })
  } finally {
    savingOrder.value = false
  }
}

// Load data on component mount
onMounted(() => {
  fetchCommodities()
})
</script>

<style scoped>
.commodity-page {
  padding-top: 1.5rem;
  padding-bottom: 3rem;
  max-width: 1200px;
  margin: 0 auto;
}

/* Custom styling for cards */
:deep(.p-card) {
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  transition: box-shadow 0.3s ease;
}

:deep(.p-card:hover) {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

:deep(.p-card .p-card-content) {
  padding: 1rem;
}

/* Dialog styling */
:deep(.p-dialog-content) {
  padding: 1.5rem;
}

/* Form field styling */
:deep(.p-float-label) {
  margin-bottom: 1rem;
}

:deep(.p-inputtext:enabled:focus),
:deep(.p-dropdown:focus),
:deep(.p-inputnumber:focus) {
  box-shadow: 0 0 0 1px var(--primary-color);
}

/* OrderList full width styling */
.favorites-orderlist {
  width: 100% !important;
}

:deep(.p-listbox) {
  width: 100% !important;
  margin-right: 3rem;
}
</style>
